import {
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
} from "@heroicons/react/20/solid";
import { JSX } from "react";

interface PaginationProps {
	pageSize: number;
	page: number;
	itemCount: number | undefined;
	totalCount?: number;
	isFiltered?: boolean;
	onChange: (page: number) => void;
	className?: string;
}

export default function SimplePagination({
	pageSize,
	page,
	itemCount,
	totalCount,
	isFiltered,
	onChange,
	className,
}: PaginationProps) {
	if (!itemCount && isFiltered) {
		return (
			<p className="mt-5 px-6 text-sm text-gray-700">
				Showing <span className="font-medium">0</span> to{" "}
				<span className="font-medium">0</span> of <span className="font-medium">0</span>{" "}
				results
				{isFiltered && totalCount && (
					<span>
						{" "}
						(filtered from <span className="font-medium">{totalCount}</span> total
						results)
					</span>
				)}
			</p>
		);
	}

	if (!itemCount || (itemCount <= pageSize && !isFiltered)) {
		return null;
	}

	const MAX_BUTTONS = 5;

	const pages = Math.ceil(itemCount / pageSize);
	const first = page * pageSize + 1;
	const last = (page + 1) * pageSize > itemCount ? itemCount : (page + 1) * pageSize;

	const pageButtons = ((pages: number, currentPage: number, onChange: (page: number) => void) => {
		if (pages == 1) {
			return null;
		}

		const buttons: JSX.Element[] = [];

		if (pages <= MAX_BUTTONS) {
			for (let i = 0; i < pages; i++) {
				buttons.push(
					<SimplePageButton
						key={i}
						page={i}
						isCurrent={i === currentPage}
						onChange={onChange}
					/>
				);
			}
		} else {
			const calcOffset = (index: number, buttons: number, pages: number) => {
				switch (true) {
					case index < Math.round(buttons / 2):
						return 0;
					case index > pages - Math.round(buttons / 2):
						return pages - buttons;
					default:
						return Math.round(index - buttons / 2);
				}
			};

			// Show "MAX_BUTTONS - 2" page buttons when more than MAX_BUTTONS pages
			const offset = calcOffset(currentPage, MAX_BUTTONS - 2, pages);
			for (let i = offset; i - offset < MAX_BUTTONS - 2; i++) {
				buttons.push(
					<SimplePageButton
						key={i}
						page={i}
						isCurrent={i === currentPage}
						onChange={onChange}
					/>
				);
			}
		}

		return buttons;
	})(pages, page, onChange);

	return (
		<div
			className={`flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 ${className}`}>
			{pages > 1 && (
				<div className="flex flex-1 justify-between sm:hidden">
					<button
						className="btn relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-default"
						disabled={page <= 0}
						onClick={() => onChange(--page)}>
						Previous
					</button>
					<button
						className="btn relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-default"
						disabled={page >= pages - 1}
						onClick={() => onChange(++page)}>
						Next
					</button>
				</div>
			)}
			<div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
				<p className={`${pages <= 1 && isFiltered ? "mt-3" : ""} text-sm text-gray-700`}>
					Showing <span className="font-medium">{first}</span> to{" "}
					<span className="font-medium">{last}</span> of{" "}
					<span className="font-medium">{itemCount}</span> results
					{isFiltered && totalCount && (
						<span>
							{" "}
							(filtered from <span className="font-medium">{totalCount}</span> total
							results)
						</span>
					)}
				</p>
				{pages > 1 && (
					<div>
						<nav
							className="isolate inline-flex -space-x-px rounded-md shadow-sm"
							aria-label="Pagination">
							{pages > MAX_BUTTONS && (
								<button
									className="btn relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0   disabled:bg-gray-100 disabled:cursor-default"
									disabled={page <= 0}
									onClick={() => onChange(0)}>
									<span className="sr-only">First</span>
									<ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
								</button>
							)}
							<button
								className="btn relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0   disabled:bg-gray-100 disabled:cursor-default"
								disabled={page <= 0}
								onClick={() => onChange(--page)}>
								<span className="sr-only">Previous</span>
								<ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
							</button>

							{pageButtons}

							<button
								className="btn relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0   disabled:bg-gray-100 disabled:cursor-default"
								disabled={page >= pages - 1}
								onClick={() => onChange(++page)}>
								<span className="sr-only">Next</span>
								<ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
							</button>
							{pages > MAX_BUTTONS && (
								<button
									className="btn relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0   disabled:bg-gray-100 disabled:cursor-default"
									disabled={page >= pages - 1}
									onClick={() => onChange(pages - 1)}>
									<span className="sr-only">Last</span>
									<ChevronDoubleRightIcon
										className="h-5 w-5"
										aria-hidden="true"
									/>
								</button>
							)}
						</nav>
					</div>
				)}
			</div>
		</div>
	);
}

interface SimplePageProps {
	page: number;
	isCurrent: boolean;
	onChange: (page: number) => void;
}

export function SimplePageButton({ page, isCurrent, onChange }: SimplePageProps) {
	const currentStyle =
		"btn relative z-10 inline-flex items-center bg-primary-700 px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600";
	const defaultStyle =
		"btn relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0";
	return (
		<button
			aria-current={isCurrent ? "page" : undefined}
			className={isCurrent ? currentStyle : defaultStyle}
			disabled={isCurrent}
			onClick={() => onChange(page)}>
			{page + 1}
		</button>
	);
}
